import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const ROLES = ['admin', 'photographer', 'dealer'];
const blankForm = { email: '', name: '', role: 'photographer', dealership_id: '', password: '' };

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);  // null | 'new' | user
  const [form, setForm] = useState(blankForm);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const flash = (m) => { setMessage(m); setTimeout(() => setMessage(''), 2800); };

  const loadUsers = useCallback(() => {
    const h = authHeader();
    if (!h) return;
    setLoading(true);
    const qs = new URLSearchParams();
    if (search.trim()) qs.append('search', search.trim());
    if (roleFilter) qs.append('role', roleFilter);
    qs.append('limit', '300');
    fetch(`${API_BASE}/admin/users?${qs}`, { headers: h })
      .then(adminJson)
      .then(d => setUsers(d.users || (Array.isArray(d) ? d : [])))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [authHeader, search, roleFilter]);

  useEffect(() => { const t = setTimeout(loadUsers, 250); return () => clearTimeout(t); }, [loadUsers]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/dealers?limit=500`, { headers: h })
      .then(adminJson)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || []))))
      .catch(() => {});
  }, [authHeader]);

  const isLocked = (u) => u.locked_until && new Date(u.locked_until) > new Date();

  const openAdd = () => { setForm(blankForm); setEditing('new'); };
  const openEdit = (u) => {
    setForm({ email: u.email, name: u.name || '', role: u.role, dealership_id: u.dealership_id || '', password: '' });
    setEditing(u);
  };

  const save = async () => {
    const h = authHeader();
    if (!h) return;
    const isNew = editing === 'new';
    const body = isNew
      ? { email: form.email.trim().toLowerCase(), name: form.name.trim(), role: form.role,
          dealership_id: form.dealership_id === '' ? null : Number(form.dealership_id), password: form.password }
      : { name: form.name.trim(), role: form.role,
          dealership_id: form.dealership_id === '' ? null : Number(form.dealership_id) };
    if (isNew && (!body.email || !body.password)) { setError('Email and password are required'); return; }
    try {
      const res = await fetch(isNew ? `${API_BASE}/admin/users` : `${API_BASE}/admin/users/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.detail || 'Save failed'); return; }
      setEditing(null); flash(isNew ? 'User created' : 'User updated'); loadUsers();
    } catch (e) { setError('Save failed'); }
  };

  const action = async (u, path, method, okMsg, body) => {
    const h = authHeader();
    if (!h) return;
    const res = await fetch(`${API_BASE}/admin/users/${u.id}${path}`, {
      method,
      headers: body ? { ...h, 'Content-Type': 'application/json' } : h,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.ok) { flash(okMsg); loadUsers(); }
    else { const e = await res.json().catch(() => ({})); setError(e.detail || 'Action failed'); }
  };

  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.email}:`);
    if (pw && pw.length >= 6) action(u, '/reset-password', 'POST', 'Password reset', { new_password: pw });
    else if (pw) setError('Password must be at least 6 characters');
  };

  return (
    <div style={{ padding: 20, maxWidth: 1150, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Users</h2>
      </div>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error} <button onClick={() => setError('')} style={linkBtn}>dismiss</button></div>}
      {message && <div style={{ color: '#059669', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <input placeholder="Search name / email…" value={search} onChange={e => setSearch(e.target.value)} style={ctrl} />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={ctrl}>
          <option value="">All roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={openAdd} style={primaryBtn}>+ Add User</button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{users.length} user(s)</span>
      </div>

      {loading ? <div>Loading…</div> : (
        <table style={table}>
          <thead><tr>{['Name', 'Email', 'Role', 'Dealership', 'Status', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #eef2f7' }}>
                <td style={td}>{u.name || <em style={{ color: '#94a3b8' }}>—</em>}</td>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.role}</td>
                <td style={td}>{u.dealership_name || '—'}</td>
                <td style={td}>
                  {u.is_active ? <span style={{ color: '#059669' }}>active</span> : <span style={{ color: '#94a3b8' }}>inactive</span>}
                  {isLocked(u) && <span style={{ color: '#dc2626', marginLeft: 6 }}>🔒 locked</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEdit(u)} style={linkBtn}>Edit</button>{' · '}
                  <button onClick={() => resetPw(u)} style={linkBtn}>Reset PW</button>{' · '}
                  {isLocked(u) && <><button onClick={() => action(u, '/unlock', 'POST', 'User unlocked')} style={linkBtn}>Unlock</button>{' · '}</>}
                  {u.is_active
                    ? <button onClick={() => window.confirm(`Deactivate ${u.email}?`) && action(u, '', 'DELETE', 'User deactivated')} style={{ ...linkBtn, color: '#dc2626' }}>Deactivate</button>
                    : <button onClick={() => action(u, '/restore', 'POST', 'User restored')} style={{ ...linkBtn, color: '#059669' }}>Restore</button>}
                </td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={6} style={{ ...td, color: '#6b7280' }}>No users.</td></tr>}
          </tbody>
        </table>
      )}

      {editing && (
        <div style={overlay} onClick={() => setEditing(null)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3>{editing === 'new' ? 'Add User' : `Edit ${editing.email}`}</h3>
            {editing === 'new' && (
              <label style={lbl}>Email<input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inp} type="email" /></label>
            )}
            <label style={lbl}>Name<input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inp} /></label>
            <div style={{ display: 'flex', gap: 10 }}>
              <label style={{ ...lbl, flex: 1 }}>Role
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={inp}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label style={{ ...lbl, flex: 1 }}>Dealership
                <select value={form.dealership_id} onChange={e => setForm({ ...form, dealership_id: e.target.value })} style={inp}>
                  <option value="">— none —</option>
                  {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
                </select>
              </label>
            </div>
            {editing === 'new' && (
              <label style={lbl}>Password<input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={inp} type="text" placeholder="min 6 chars" /></label>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button onClick={save} style={primaryBtn}>Save</button>
              <button onClick={() => setEditing(null)} style={btn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ctrl = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6 };
const btn = { padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const primaryBtn = { ...btn, background: '#0078D4', color: '#fff', border: 'none' };
const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#fff', borderRadius: 10, padding: 22, width: 420, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 8 };
const inp = { width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 2 };
